import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AddTaskIcon from '@mui/icons-material/AddTask';
import { format, addDays } from '../../utils/datetime';
import {
  createTokenRequest,
  clearTokensError,
  clearRevealedToken,
} from '../../redux/actions/auth/apiTokens';
import PageBreadcrumbs from '../common/PageBreadcrumbs';
import ScopePicker from './ScopePicker';
import TokenRevealDialog from './TokenRevealDialog';
import {
  DEFAULT_SCOPES,
  EXPIRY_OPTIONS,
  MAX_TOKEN_NAME_LENGTH,
} from './apiTokenConstants';

/**
 * Full-page token creation, styled like the goal creation page: breadcrumb
 * header, outlined cards, selectable option cards and a bottom action bar.
 * On success the one-time reveal dialog opens here; closing it returns to
 * the tokens list.
 */
const CreateAPITokenPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(() => [...DEFAULT_SCOPES]);
  const [expiresInDays, setExpiresInDays] = useState(365);

  const { isCreating, error, revealedToken } = useSelector(
    (state) => state.auth.apiTokens
  );

  // Fresh slate on mount: no stale error or lingering one-time secret.
  useEffect(() => {
    dispatch(clearTokensError());
    dispatch(clearRevealedToken());
  }, [dispatch]);

  const handleScopeToggle = (scope) => {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    );
  };

  const handleScopesBulk = (values, checked) => {
    setScopes((prev) =>
      checked
        ? [...new Set([...prev, ...values])]
        : prev.filter((s) => !values.includes(s))
    );
  };

  const nameTooLong = name.trim().length > MAX_TOKEN_NAME_LENGTH;
  const canSubmit =
    !isCreating && name.trim().length > 0 && !nameTooLong && scopes.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    dispatch(
      createTokenRequest({
        name: name.trim(),
        scopes,
        expires_in_days: expiresInDays,
      })
    );
  };

  const handleRevealClose = () => {
    dispatch(clearRevealedToken());
    navigate('/settings/api-keys');
  };

  const expiryDate = useMemo(
    () => (expiresInDays ? format(addDays(new Date(), expiresInDays), 'MMM d, yyyy') : null),
    [expiresInDays]
  );

  const accent = theme.palette.primary.main;

  const CardHead = ({ icon, title, helper }) => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
        <Box sx={{ display: 'flex', color: 'primary.main' }}>{icon}</Box>
        <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
      </Stack>
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1500, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
      >
        <PageBreadcrumbs
          items={[
            { text: 'API Tokens', icon: <KeyIcon />, link: '/settings/api-keys' },
            { text: 'New token' },
          ]}
          mb={0}
        />
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => dispatch(clearTokensError())}>
          {typeof error === 'string' ? error : error.message || 'Failed to create token'}
        </Alert>
      )}

      <Box sx={{ mt: 1.5 }}>
        {/* Row 1: Details + Expiry */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <Stack spacing={2.5}>
              <Box>
                <CardHead
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  title="Token name"
                  helper="A descriptive name so you can recognise this token later."
                />
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  value={name}
                  placeholder="e.g., Production Server Integration"
                  onChange={(e) => setName(e.target.value)}
                  error={nameTooLong}
                  helperText={
                    nameTooLong
                      ? `Name must be at most ${MAX_TOKEN_NAME_LENGTH} characters`
                      : undefined
                  }
                />
              </Box>

              <Box>
                <CardHead
                  icon={<LockOutlinedIcon fontSize="small" sx={{ color: theme.palette.warning.main }} />}
                  title="Shown once"
                  helper="The secret is displayed a single time after creation. Store it somewhere safe — you can always regenerate it later."
                />
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
            <CardHead
              icon={<ScheduleIcon fontSize="small" />}
              title="Expiry"
              helper="Shorter lifetimes are safer. The expiry clock restarts if you regenerate the secret."
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
              {EXPIRY_OPTIONS.map((option) => {
                const active = expiresInDays === option.value;
                return (
                  <Box
                    key={option.value ?? 'never'}
                    onClick={() => setExpiresInDays(option.value)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') setExpiresInDays(option.value);
                    }}
                    sx={{
                      cursor: 'pointer',
                      position: 'relative',
                      p: 1.25,
                      borderRadius: 2,
                      border: `1.5px solid ${active ? accent : theme.palette.divider}`,
                      bgcolor: active ? alpha(accent, 0.1) : 'transparent',
                      transition: 'all 120ms ease',
                      '&:hover': { borderColor: accent, bgcolor: alpha(accent, 0.06) },
                    }}
                  >
                    {active && (
                      <CheckCircleIcon
                        sx={{ position: 'absolute', top: 8, right: 8, fontSize: 16, color: accent }}
                      />
                    )}
                    <Typography variant="body2" fontWeight={700} sx={{ pr: active ? 2.5 : 0 }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                      {option.value
                        ? `Until ${format(addDays(new Date(), option.value), 'MMM d, yyyy')}`
                        : 'Valid until revoked'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Box>

        {/* Row 2: Permissions */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 0.5 }}
          >
            <CardHead
              icon={<KeyIcon fontSize="small" />}
              title="Permissions (scopes)"
              helper="Select what this token can access. You can edit these later without changing the secret."
            />
            <Chip
              size="small"
              variant="outlined"
              color={scopes.length > 0 ? 'primary' : 'default'}
              label={`${scopes.length} selected`}
              sx={{ fontWeight: 700 }}
            />
          </Stack>
          <ScopePicker
            columns
            selected={scopes}
            onToggle={handleScopeToggle}
            onToggleMany={handleScopesBulk}
          />
          {scopes.length === 0 && (
            <Typography variant="caption" color="error">
              Select at least one scope
            </Typography>
          )}
        </Paper>

        {/* Row 3: Action bar */}
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, mt: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography variant="subtitle2" fontWeight={700}>
                {name.trim() || 'New API token'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {scopes.length} scope{scopes.length === 1 ? '' : 's'}
                {' · '}
                {expiryDate ? `expires ${expiryDate}` : 'never expires'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Button onClick={() => navigate('/settings/api-keys')} disabled={isCreating}>
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : <AddTaskIcon />}
                disabled={!canSubmit}
                onClick={submit}
              >
                {isCreating ? 'Creating…' : 'Create token'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>

      {/* One-time secret reveal; closing returns to the tokens list */}
      <TokenRevealDialog
        open={Boolean(revealedToken)}
        tokenPayload={revealedToken}
        onClose={handleRevealClose}
      />
    </Box>
  );
};

export default CreateAPITokenPage;
